import type {
  ActionInstructions,
  BeneficiaryTypeEnum,
  DeliveryMethod,
  DeliveryTiming,
  PpaActionTransactionType,
} from "@/types";
import { endOfMonth, startOfMonth } from "date-fns";
import { formatIsoDate, parseIsoDate } from "./ppaPortfolioUtils";
import { createTodayDateSpec, getNextYearEndDate } from "./ppaDateUtils";

/**
 * Action instructions utility functions for PPA components
 */

// GOs are issued in monthly buckets, so the allocator only operates on whole
// calendar months. Both manual-entry and AI-extraction paths must store
// start_date/end_date on a month boundary or the backend rejects the save
// (see app/portfolio/api/portfolio_validators for monthly/quarterly PPAs).
// Returns null on empty / unparseable input so callers can distinguish
// "nothing to assign" from a legitimate boundary.
export function snapToMonthStart(
  raw: string | null | undefined
): string | null {
  if (!raw) return null;
  const parsed = parseIsoDate(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatIsoDate(startOfMonth(parsed));
}

export function snapToMonthEnd(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const parsed = parseIsoDate(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatIsoDate(endOfMonth(parsed));
}

/**
 * Create default action instructions for new PPA portfolios
 */
export function createDefaultActionInstructions(): ActionInstructions {
  return {
    delivery_method: "AUTOMATIC" as DeliveryMethod,
    delivery_timing: "YEARLY" as DeliveryTiming,
    recurring_date_specs: [createTodayDateSpec()], // Default to one date for yearly timing
    end_date: getNextYearEndDate(),
    transaction_type: "SEND" as PpaActionTransactionType,
    custom_user_upload_ids: [],
    // CRITICAL: Explicitly set templates to null for backend validation
    send_template: {
      recipient_company_name: "",
      recipient_registry_country: "",
      recipient_eecs_account_number: "",
      recipient_registry_internal_account_number: null,
    },
    cancel_statement: null as any, // Must be null for SEND transaction type
    notes: "",
  };
}

/**
 * Initialize recurring date specs based on delivery timing
 */
export function initializeRecurringDateSpecs(
  instructions: ActionInstructions,
  deliveryTiming: DeliveryTiming
): ActionInstructions {
  const updated = { ...instructions };
  updated.delivery_timing = deliveryTiming;

  const expectedCount = getExpectedDateCount(deliveryTiming);
  const currentCount = updated.recurring_date_specs.length;

  if (expectedCount === -1) {
    // FIXED_DATES - keep existing or add one if empty
    if (currentCount === 0) {
      updated.recurring_date_specs = [createTodayDateSpec()];
    }
  } else {
    // Fixed count - adjust array to match expected count
    const newSpecs = [...updated.recurring_date_specs];

    if (currentCount < expectedCount) {
      // Add missing specs with smart defaults based on timing type
      if (deliveryTiming === "HALF_YEAR") {
        // H1 (Jan 1st), H2 (Jul 1st) - logical month order
        newSpecs.splice(
          0,
          newSpecs.length,
          { day: 1, month: 1 }, // H1 (first picker)
          { day: 1, month: 7 } // H2 (second picker)
        );
      } else if (deliveryTiming === "QUARTERLY") {
        // Q1, Q2, Q3, Q4 order - logical month order
        newSpecs.splice(
          0,
          newSpecs.length,
          { day: 1, month: 1 }, // Q1 (first picker)
          { day: 1, month: 4 }, // Q2 (second picker)
          { day: 1, month: 7 }, // Q3 (third picker)
          { day: 1, month: 10 } // Q4 (fourth picker)
        );
      } else {
        // For YEARLY and MONTHLY - fill to expected count
        for (let i = currentCount; i < expectedCount; i++) {
          const newSpec = createTodayDateSpec();
          // For MONTHLY delivery, always use month=1 (month is ignored for monthly timing)
          if (deliveryTiming === "MONTHLY") {
            newSpec.month = 1;
          }
          newSpecs.push(newSpec);
        }
      }
    } else if (currentCount > expectedCount) {
      // Remove extra specs
      newSpecs.splice(expectedCount);
    }

    updated.recurring_date_specs = newSpecs;
  }

  // For MONTHLY delivery, ensure existing specs also have month=1
  if (deliveryTiming === "MONTHLY" && updated.recurring_date_specs.length > 0) {
    updated.recurring_date_specs = updated.recurring_date_specs.map((spec) => ({
      ...spec,
      month: 1,
    }));
  }

  return updated;
}

/**
 * Get expected number of dates for delivery timing type
 */
export function getExpectedDateCount(deliveryTiming: DeliveryTiming): number {
  switch (deliveryTiming) {
    case "YEARLY":
      return 1;
    case "HALF_YEAR":
      return 2;
    case "QUARTERLY":
      return 4;
    case "MONTHLY":
      return 1;
    case "FIXED_DATES":
      return -1; // Variable count
    default:
      return 1;
  }
}

/**
 * Initialize transaction-specific templates when transaction type changes
 */
export function initializeTransactionTemplates(
  instructions: ActionInstructions,
  transactionType: PpaActionTransactionType
): ActionInstructions {
  const updated = { ...instructions };
  updated.transaction_type = transactionType;

  if (transactionType === "SEND") {
    // CRITICAL: Must explicitly set to null (not undefined) to satisfy backend validation
    updated.cancel_statement = null;
    if (!updated.send_template) {
      updated.send_template = {
        recipient_company_name: "",
        recipient_registry_country: "",
        recipient_eecs_account_number: "",
        recipient_registry_internal_account_number: null,
      };
    }
  } else if (transactionType === "CANCEL") {
    // CRITICAL: Must explicitly set to null (not undefined) to satisfy backend validation
    updated.send_template = null;
    if (!updated.cancel_statement) {
      updated.cancel_statement = {
        temp_id: `temp-${Date.now()}`,
        beneficiary_name: "",
        beneficiary_type: "CONSUMER" as BeneficiaryTypeEnum,
        registry_code: "",
        cancellation_purpose: "",
        mwh_cancelled: null,
      };
    }
  }

  return updated;
}
